import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import KpiCard from "../components/KpiCard";
import { api, fmtUSD, fmtPct } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ExternalLink, ChevronDown, ChevronRight, ListTree } from "lucide-react";
import StudyAnalysis from "../components/StudyAnalysis";

const marginBadge = (m) =>
  m >= 50 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
  : m >= 20 ? "bg-amber-100 text-amber-800 border-amber-200"
  : "bg-red-100 text-red-800 border-red-200";

export function StudiesList() {
  const [data, setData] = useState([]);
  const [ranking, setRanking] = useState([]);
  useEffect(() => {
    api.get("/pnl/studies/all").then((r) => setData(r.data));
    api.get("/pnl/sponsor-ranking").then((r) => setRanking(r.data));
  }, []);

  return (
    <AppShell
      title="Study Profitability"
      subtitle="True profit per study · Which sponsors are worth running trials for?"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-md shadow-sm overflow-auto">
          <div className="px-5 py-4 border-b border-border"><h3 className="font-semibold">All Studies</h3></div>
          <table className="w-full text-sm stryde-table">
            <thead>
              <tr>
                <th className="text-left px-4 py-2.5">Study</th>
                <th className="text-left px-4 py-2.5">Sponsor</th>
                <th className="text-right px-4 py-2.5">Revenue</th>
                <th className="text-right px-4 py-2.5">Net Profit</th>
                <th className="text-right px-4 py-2.5">Gross Margin</th>
                <th className="text-right px-4 py-2.5">ROI</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.cta_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-semibold">{s.study_name}</td>
                  <td className="px-4 py-2.5 text-slate-700">{s.sponsor}</td>
                  <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(s.gross_revenue)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono-fin ${s.net_profit >= 0 ? "text-slate-900" : "text-red-600"}`}>{fmtUSD(s.net_profit)}</td>
                  <td className="px-4 py-2.5 text-right"><Badge variant="outline" className={marginBadge(s.gross_margin_percent)}>{fmtPct(s.gross_margin_percent)}</Badge></td>
                  <td className="px-4 py-2.5 text-right font-mono-fin font-semibold">{fmtPct(s.roi_score)}</td>
                  <td className="px-4 py-2.5 text-right"><Link to={`/studies/${s.cta_id}`}><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Detail</Button></Link></td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No studies yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-border rounded-md shadow-sm">
          <div className="px-5 py-4 border-b border-border"><h3 className="font-semibold">Sponsor Profitability Ranking</h3></div>
          <div className="p-5 space-y-3">
            {ranking.map((r) => (
              <div key={r.sponsor} className="flex items-center justify-between pb-3 border-b border-slate-100 last:border-0">
                <div>
                  <div className="font-semibold text-sm">{r.sponsor}</div>
                  <div className="text-xs text-slate-500">{r.studies} studies</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-fin font-semibold">{fmtUSD(r.profit)}</div>
                  <div className="text-xs font-mono-fin text-slate-500">{fmtPct(r.margin_percent)} margin</div>
                </div>
              </div>
            ))}
            {ranking.length === 0 && <div className="text-sm text-slate-500">No sponsors yet.</div>}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

export function StudyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [cta, setCta] = useState(null);
  useEffect(() => {
    api.get(`/pnl/study/${id}`).then((r) => setData(r.data));
    api.get(`/ctas/${id}`).then((r) => setCta(r.data)).catch(() => setCta(null));
  }, [id]);

  if (!data) return <AppShell title="Study"><div>Loading...</div></AppShell>;

  const costs = [
    { name: "PI", value: data.pi_distribution },
    { name: "Clinic", value: data.clinic_fee },
    { name: "Labor", value: data.labor_cost },
    { name: "Advertising", value: data.advertising },
    { name: "IRB", value: data.irb_fees },
    { name: "Supplies/Eq", value: data.supplies_equipment },
    { name: "Other", value: data.other_direct },
  ];

  return (
    <AppShell
      title={data.study_name}
      subtitle={`${data.sponsor} · ${data.therapeutic_area}`}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="CTA Value" value={fmtUSD(data.cta_total_value)} accent="#0A4C95" />
        <KpiCard label="Gross Revenue" value={fmtUSD(data.gross_revenue)} sub={`Collected: ${fmtUSD(data.collected)}`} accent="#10B981" />
        <KpiCard label="Outstanding AR" value={fmtUSD(data.outstanding_ar)} accent="#F05D23" />
        <KpiCard label="Net Profit" value={fmtUSD(data.net_profit)} sub={`Margin: ${fmtPct(data.gross_margin_percent)}`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5">
        <KpiCard label="Labor % Revenue" value={fmtPct(data.labor_pct_of_revenue)} sub="Target < 20%" />
        <KpiCard label="Startup Recovery" value={fmtPct(data.startup_recovery_percent)} sub="Target 100%" />
        <KpiCard label="ROI Score" value={fmtPct(data.roi_score)} accent="#F05D23" />
        <KpiCard label="Total Cost" value={fmtUSD(data.total_cost)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white border border-border rounded-md p-6 shadow-sm">
          <h3 className="font-semibold">Cost Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costs} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={80} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Bar dataKey="value" fill="#0A4C95" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-border rounded-md p-6 shadow-sm">
          <h3 className="font-semibold">Thresholds & Alerts</h3>
          <div className="divide-y divide-slate-100 mt-2 text-sm">
            <div className="py-3 flex justify-between"><span>Gross Margin (target &gt;50%)</span><Badge variant="outline" className={marginBadge(data.gross_margin_percent)}>{fmtPct(data.gross_margin_percent)}</Badge></div>
            <div className="py-3 flex justify-between"><span>Labor Cost % Rev (target &lt;20%)</span><Badge variant="outline" className={data.labor_pct_of_revenue < 20 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : data.labor_pct_of_revenue < 40 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200"}>{fmtPct(data.labor_pct_of_revenue)}</Badge></div>
            <div className="py-3 flex justify-between"><span>Startup Recovery (target 100% by SIV)</span><Badge variant="outline" className={data.startup_recovery_percent >= 100 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}>{fmtPct(data.startup_recovery_percent)}</Badge></div>
            <div className="py-3 flex justify-between"><span>Outstanding A/R on this study</span><span className="font-mono-fin font-semibold">{fmtUSD(data.outstanding_ar)}</span></div>
          </div>
        </div>
      </div>

      {/* Visit-level Schedule of Events (with activities) */}
      {cta && (cta.visit_grants?.length > 0 || cta.invoicables?.length > 0) && (
        <div className="mt-8">
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-lg font-semibold text-slate-900">Schedule of Events</h2>
            <span className="text-xs text-slate-500">
              {cta.visit_grants?.length || 0} visits ·{" "}
              {(cta.visit_grants || []).reduce((s, v) => s + (v.activities?.length || 0), 0)} activities
            </span>
          </div>
          <VisitsWithActivities visits={cta.visit_grants || []} />
        </div>
      )}

      {/* Embedded budget/rate-card analysis */}
      <div className="mt-8">
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="text-lg font-semibold text-slate-900">Budget Analysis (vs Rate Card)</h2>
          <span className="text-xs text-slate-500">P75 · P80 · P90 benchmarks for every cost item</span>
        </div>
        <StudyAnalysis ctaId={id} showPicker={false} embedded />
      </div>
    </AppShell>
  );
}


function VisitsWithActivities({ visits }) {
  const [openSet, setOpenSet] = useState(() => new Set(visits.map((_, i) => i)));
  const toggle = (i) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <div className="bg-white border border-border rounded-md shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <ListTree className="w-4 h-4 text-[#0A4C95]" />
        <span className="text-sm font-semibold">Visit grants &amp; per-procedure breakdown</span>
        <div className="flex-1" />
        <button
          onClick={() => setOpenSet(openSet.size === visits.length ? new Set() : new Set(visits.map((_, i) => i)))}
          className="text-xs text-slate-500 hover:text-[#0A4C95]"
          data-testid="visits-toggle-all"
        >
          {openSet.size === visits.length ? "Collapse all" : "Expand all"}
        </button>
      </div>
      <table className="w-full text-sm stryde-table">
        <thead>
          <tr>
            <th className="text-left px-4 py-2 w-[36px]"></th>
            <th className="text-left px-4 py-2 w-[80px]">Code</th>
            <th className="text-left px-4 py-2">Visit / Activity</th>
            <th className="text-right px-4 py-2 w-[140px]">Amount</th>
            <th className="text-left px-4 py-2 w-[160px]">Frequency</th>
          </tr>
        </thead>
        <tbody>
          {visits.map((v, i) => {
            const activities = v.activities || [];
            const subTotal = activities.reduce((s, a) => s + (a.amount || 0), 0);
            const isOpen = openSet.has(i);
            return (
              <React.Fragment key={`${v.visit_code}-${i}`}>
                <tr
                  className="border-t border-slate-100 bg-slate-50 cursor-pointer hover:bg-slate-100"
                  onClick={() => activities.length > 0 && toggle(i)}
                  data-testid={`visit-row-${i}`}
                >
                  <td className="px-4 py-2 align-middle">
                    {activities.length > 0 ? (
                      isOpen
                        ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                        : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                    ) : null}
                  </td>
                  <td className="px-4 py-2 font-semibold text-slate-700">{v.visit_code}</td>
                  <td className="px-4 py-2 font-semibold text-slate-900">
                    {v.visit_name}
                    {activities.length > 0 && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                        {activities.length} item{activities.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono-fin font-semibold">
                    {fmtUSD(v.grant_amount || 0)}
                    {activities.length > 0 && Math.abs((v.grant_amount || 0) - subTotal) > 1 && (
                      <div className="text-[10px] text-amber-600">
                        sum of items: {fmtUSD(subTotal)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{v.frequency || "per patient"}</td>
                </tr>
                {isOpen && activities.map((a, j) => (
                  <tr key={`act-${i}-${j}`} className="border-t border-slate-100">
                    <td className="px-4 py-1.5"></td>
                    <td className="px-4 py-1.5"></td>
                    <td className="px-4 py-1.5 text-slate-700 pl-8">
                      <span className="text-slate-400 mr-2">└</span>{a.name}
                      {a.description && <div className="text-[10px] text-slate-400 ml-4">{a.description}</div>}
                    </td>
                    <td className="px-4 py-1.5 text-right font-mono-fin text-slate-700">
                      {a.amount ? fmtUSD(a.amount) : "—"}
                    </td>
                    <td className="px-4 py-1.5 text-xs text-slate-400">{a.frequency || ""}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
